import { useState, useRef, lazy, Suspense } from "react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { supabase } from "@/integrations/supabase/client";
import { toast } from "sonner";
import { z } from "zod";
import { ProposalContent, ProposalImages } from "@/types/proposal";

const SignatureCanvas = lazy(() => import("react-signature-canvas"));

// Input validation schema
const signatureSchema = z.object({
  clientName: z
    .string()
    .trim()
    .min(1, { message: "Client name is required" })
    .max(100, { message: "Name must be less than 100 characters" }),
  clientTitle: z
    .string()
    .trim()
    .max(100, { message: "Title must be less than 100 characters" })
    .optional(),
  clientEmail: z
    .string()
    .trim()
    .email({ message: "Invalid email address" })
    .max(255, { message: "Email must be less than 255 characters" })
    .optional()
    .or(z.literal("")),
});

interface SignatureSectionProps {
  proposalName: string;
  content: ProposalContent;
  images: ProposalImages;
  isClientMode: boolean;
  creatorUserId?: string;
}

export const SignatureSection = ({ proposalName, content, images, isClientMode, creatorUserId }: SignatureSectionProps) => {
  const sigCanvas = useRef<any>(null);
  const [clientName, setClientName] = useState("");
  const [clientTitle, setClientTitle] = useState("");
  const [clientEmail, setClientEmail] = useState("");
  const [isSaving, setIsSaving] = useState(false);

  const handleClear = () => {
    sigCanvas.current?.clear();
  };

  const handleSave = async () => {
    if (sigCanvas.current?.isEmpty()) {
      toast.error("Please provide a signature");
      return;
    }

    // Validate inputs
    const validation = signatureSchema.safeParse({ 
      clientName: clientName.trim(),
      clientTitle: clientTitle.trim(),
      clientEmail: clientEmail.trim()
    });

    if (!validation.success) {
      const firstError = validation.error.errors[0];
      toast.error(firstError.message);
      return;
    }

    // Validate creator_user_id is present
    if (!creatorUserId) {
      toast.error("Invalid proposal link. Please contact the sender for a valid link.");
      return;
    }

    setIsSaving(true);
    
    try {
      const signatureData = sigCanvas.current.toDataURL();
      
      // Submit signature through secure edge function
      const { data, error } = await supabase.functions.invoke("submit-signature", {
        body: {
          proposalName,
          clientName: validation.data.clientName,
          clientTitle: validation.data.clientTitle || null,
          clientEmail: validation.data.clientEmail || null,
          signatureData,
          proposalContent: { content, images },
          creatorUserId,
        },
      });

      if (error) throw error;
      if (!data?.success) throw new Error(data?.error || "Failed to submit signature");

      toast.success("Proposal signed successfully!");
      
      // Clear the form
      sigCanvas.current.clear();
      setClientName("");
      setClientTitle("");
      setClientEmail("");
    } catch (error: any) {
      // Security: Don't log full error details
      console.error("Signature submission error:", error);
      toast.error(error.message || "Failed to save signature. Please try again.");
    } finally {
      setIsSaving(false);
    }
  };

  if (!isClientMode) {
    return null;
  }

  return (
    <div className="signature-section border-t-2 border-slate-800 pt-8 mt-8 print:hidden">
      <h3 className="text-xl font-bold text-slate-800 mb-6 text-center">
        ACCEPTANCE & SIGNATURE
      </h3>
      
      <div className="grid grid-cols-2 gap-8 mb-6">
        <div className="space-y-4">
          <div>
            <Label htmlFor="clientName" className="text-sm font-medium text-slate-700">
              Client Name *
            </Label>
            <Input
              id="clientName"
              value={clientName}
              onChange={(e) => setClientName(e.target.value)}
              placeholder="Enter your full name"
              className="mt-1"
            />
          </div>
          
          <div>
            <Label htmlFor="clientTitle" className="text-sm font-medium text-slate-700">
              Title / Position
            </Label>
            <Input
              id="clientTitle"
              value={clientTitle}
              onChange={(e) => setClientTitle(e.target.value)}
              placeholder="e.g., Homeowner, Project Manager"
              className="mt-1"
            />
          </div>
          
          <div>
            <Label htmlFor="clientEmail" className="text-sm font-medium text-slate-700">
              Email Address
            </Label>
            <Input
              id="clientEmail"
              type="email"
              value={clientEmail}
              onChange={(e) => setClientEmail(e.target.value)}
              placeholder="your@email.com (optional)"
              className="mt-1"
            />
          </div>
          
          <div>
            <Label className="text-sm font-medium text-slate-700">
              Date
            </Label>
            <Input
              value={new Date().toLocaleDateString()}
              disabled
              className="mt-1 bg-slate-50"
            />
          </div>
        </div>

        <div>
          <Label className="text-sm font-medium text-slate-700 mb-2 block">
            Signature *
          </Label>
          <div className="border-2 border-slate-300 rounded bg-white">
            <Suspense fallback={<div className="w-full h-40 flex items-center justify-center text-slate-400">Loading signature pad...</div>}>
              <SignatureCanvas
                ref={sigCanvas}
                canvasProps={{
                  className: "w-full h-40",
                }}
              />
            </Suspense>
          </div>
          <div className="flex gap-2 mt-2">
            <Button
              type="button"
              variant="outline"
              size="sm"
              onClick={handleClear}
              className="flex-1"
            >
              Clear
            </Button>
            <Button
              type="button"
              onClick={handleSave}
              disabled={isSaving}
              className="flex-1"
            >
              {isSaving ? "Saving..." : "Accept & Sign"}
            </Button>
          </div>
        </div>
      </div>

      <p className="text-xs text-slate-500 text-center">
        By signing above, you acknowledge and accept the terms outlined in this proposal.
      </p>
    </div>
  );
};
